import { CLIPBOARD_TYPE_META } from './clipboard-constants';

export function htmlToPlainText(html) {
  if (!html) {
    return '';
  }
  const tmp = document.createElement('div');
  tmp.innerHTML = String(html);
  return (tmp.textContent || tmp.innerText || '').replace(/\u00a0/g, ' ').trim();
}

/** Extract a 10-character Amazon ASIN from saved content. */
export function extractAsin(content) {
  const trimmed = String(content || '').trim();
  const match = trimmed.match(/\b([A-Z0-9]{10})\b/i);
  return match ? match[1].toUpperCase() : null;
}

export function getAmazonUrl(asin) {
  if (!asin) {
    return null;
  }
  return `https://www.amazon.com/dp/${asin}`;
}

/** Display GTIN with spaced groups (e.g. 0 0000 0000 0000). */
export function formatGtinDisplay(content) {
  const digits = String(content || '').replace(/\D/g, '');
  if (!digits) {
    return String(content || '').trim();
  }
  if (digits.length === 13) {
    return `${digits[0]} ${digits.slice(1, 5)} ${digits.slice(5, 9)} ${digits.slice(9, 13)}`;
  }
  if (digits.length === 14) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8, 12)} ${digits.slice(12, 14)}`;
  }
  if (digits.length === 12) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8, 12)}`;
  }
  if (digits.length === 8) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 8)}`;
  }
  return digits;
}

export function getDisplayContent(item) {
  if (!item) {
    return '';
  }
  if (item.type === 'gtin') {
    return formatGtinDisplay(item.content);
  }
  if (item.type === 'asin') {
    return extractAsin(item.content) || String(item.content || '').trim();
  }
  return String(item.content || '').trim();
}

export function usesMonoBody(type) {
  return type === 'asin' || type === 'gtin';
}

/**
 * Whether an item's title is worth showing as a bold label above its content. Items
 * saved without a user-typed label get a title auto-derived (front + back end) from the
 * content's first line — a (possibly …-truncated) prefix of the body. Rendering that
 * just duplicates the content, so treat it as "no meaningful title" and let the UI hide
 * the redundant label + separator. A real, distinct label still shows. (POR-13563-2)
 */
export function hasMeaningfulTitle(item) {
  const title = String((item && item.title) || '').trim();
  if (!title) {
    return false;
  }
  const content = String((item && item.content) || '').trim();
  if (!content) {
    return true;
  }
  const bare = title.replace(/…$/, '').trim();
  return bare !== '' && !content.startsWith(bare);
}

/**
 * Relative saved date for list cards (Today, 2d ago, 1w ago, M/D/YYYY).
 * @param {string|Date} createdAt
 * @param {Date} [now]
 */
export function formatClipboardSavedAt(createdAt, now = new Date()) {
  if (!createdAt) {
    return '';
  }

  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfCreated = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor((startOfToday - startOfCreated) / 86400000);

  if (diffDays === 0) {
    return 'Today';
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }

  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 5) {
    return `${diffWeeks}w ago`;
  }

  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

export function getClipboardItemTimestamp(item) {
  return item?.created_at || item?.createdAt || item?.updated_at || item?.updatedAt || null;
}

export function matchesClipboardSearch(item, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) {
    return true;
  }

  const typeLabel = (CLIPBOARD_TYPE_META[item.type]?.label || item.type || '').toLowerCase();
  const title = String(item.title || '').toLowerCase();
  const content = String(item.content || '').toLowerCase();
  const display = getDisplayContent(item).toLowerCase();

  return typeLabel.includes(q)
    || title.includes(q)
    || content.includes(q)
    || display.includes(q);
}

export function linkifyText(text) {
  const escaped = String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  return escaped.replace(/https?:\/\/[^\s<>"']+/g, (url) => {
    const clean = url.replace(/[.,;:!?)]+$/, '');
    const tail = url.slice(clean.length);
    return `<a href="${clean}" target="_blank" rel="noopener noreferrer" class="clipboard-note-link">${clean}</a>${tail}`;
  });
}

export function sortClipboardItems(items, sortOrder) {
  const list = [...items];
  list.sort((a, b) => {
    const aTime = new Date(getClipboardItemTimestamp(a) || 0).getTime();
    const bTime = new Date(getClipboardItemTimestamp(b) || 0).getTime();
    return sortOrder === 'oldest' ? aTime - bTime : bTime - aTime;
  });
  return list;
}
